import { computed, Ref, unref } from 'vue'
import { ApolloError, ServerError, ServerParseError } from '@apollo/client/core'
import { GraphQLFormattedError } from 'graphql/error'
import { cleanStrict } from '@/utils/tools'

type ApolloErrorType = ApolloError | null
type NetError = Error | never[] | ServerParseError | ServerError | null

function unpackErrors(obj) {
  for (const [key, value] of Object.entries(obj)) {
    if (value instanceof Array) obj[key] = value[0]
  }
  return obj
}

function validationErrors(graphQLErrors: ReadonlyArray<GraphQLFormattedError> | never[]) {
  return unpackErrors(
    graphQLErrors
      ?.filter(({ extensions: { validation } = {} }) => validation)
      ?.map(({ extensions }) => extensions?.validation)
      // @ts-ignore
      .reduce((prev, cur) => ({ ...prev, ...cur }), {}),
  )
}

function networkErrors(networkError: NetError) {
  if (!networkError) return {}
  const { statusCode, response, result } = (networkError as any) ?? {}

  const { errors } = result ?? {}
  const err = errors
    ?.map(({ message, extensions: { code } }) => ({ [code]: message }))
    .reduce((prev, cur) => ({ ...prev, ...cur }), {})
  const { statusText } = response ?? {}
  return cleanStrict({ statusCode, statusText, ...err })
}

/**
 * Creates a set of error extractors that makes it easy to work with apollo errors without the need for large amounts
 * of custom data logic.
 * In practice it can be used like this:
 * const { mutate, error } = useMutation(mutationQuery)
 * const { validationErrors } = useApolloError(error)
 *
 * console.log(validationErrors.value.myError)
 *
 * @param apolloError Ref<ApolloErrorType> | ApolloErrorType
 * @return {validationErrors: ComputedRef<Object>, networkErrors: ComputedRef<Object>}
 */
export default function useApolloError(apolloError: Ref<ApolloErrorType> | ApolloErrorType) {
  // There exists another type of error called clientError but I have not been able to add it as it seems to never fire
  // const clientErr = computed(() => {
  //   const { clientErrors } = unref(apolloError) ?? {  clientErrors: [] }
  //
  //   return clientErrors
  // })

  const netErr = computed(() => {
    const { networkError } = unref(apolloError) ?? { networkError: [] }

    return networkErrors(networkError)
  })

  const validErr = computed(() => {
    const { graphQLErrors } = unref(apolloError) ?? { graphQLErrors: [] }

    return validationErrors(graphQLErrors)
  })

  return { validationErrors: validErr, networkErrors: netErr }
}
